import React, { useState, useEffect } from 'react';
import { X, Link2, ExternalLink } from 'lucide-react';

// ── helpers (duplicated from SetlistTab to keep modal self-contained) ───────

const getDomain = (url) => {
  try { return new URL(url).hostname.replace('www.', ''); }
  catch { return url; }
};
const getYouTubeId = (url) => {
  try {
    const u = new URL(url);
    if (u.hostname === 'youtu.be') return u.pathname.slice(1).split('?')[0];
    return u.searchParams.get('v') || '';
  } catch { return ''; }
};
const isYouTube = (url) => /youtube\.com|youtu\.be/i.test(url);
const normalize = (raw) => {
  const s = raw.trim();
  if (!s) return '';
  return s.startsWith('http') ? s : `https://${s}`;
};

// ── live preview ──────────────────────────────────────────────────────────────

const LinkPreview = ({ url, label }) => {
  const ytId = isYouTube(url) ? getYouTubeId(url) : null;
  if (ytId) {
    return (
      <div className="rounded-xl overflow-hidden border border-red-200 dark:border-red-500/30 bg-white dark:bg-slate-800 shadow-sm" style={{ width: '160px' }}>
        <div className="relative">
          <img src={`https://img.youtube.com/vi/${ytId}/mqdefault.jpg`}
            alt={label || 'YouTube Video'}
            className="w-full h-[90px] object-cover bg-slate-200 dark:bg-slate-700"
            onError={(e) => { e.currentTarget.style.visibility = 'hidden'; }} />
          <div className="absolute inset-0 flex items-center justify-center bg-black/20">
            <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
              <svg viewBox="0 0 24 24" className="w-4 h-4 fill-white ml-0.5"><polygon points="5,3 19,12 5,21"/></svg>
            </div>
          </div>
        </div>
        <div className="px-2.5 py-2">
          <p className="text-xs font-medium text-slate-900 dark:text-white truncate">{label || 'YouTube Video'}</p>
          <p className="text-[10px] text-red-500 font-medium mt-0.5">YouTube</p>
        </div>
      </div>
    );
  }
  return (
    <div className="inline-flex items-center gap-1.5 pl-2.5 pr-3 py-1.5 rounded-lg border border-slate-200 dark:border-white/10 bg-white dark:bg-slate-800 shadow-sm text-xs font-medium text-slate-700 dark:text-slate-200">
      <img src={`https://www.google.com/s2/favicons?domain=${getDomain(url)}&sz=32`}
        alt="" className="w-3.5 h-3.5 rounded-sm flex-shrink-0"
        onError={(e) => { e.currentTarget.style.display = 'none'; }} />
      <span className="max-w-[200px] truncate">{label || getDomain(url)}</span>
      <ExternalLink className="w-3 h-3 text-slate-400 flex-shrink-0" />
    </div>
  );
};

// ── modal ─────────────────────────────────────────────────────────────────────

const AddLinkModal = ({ songTitle, onAdd, onClose }) => {
  const [url, setUrl] = useState('');
  const [label, setLabel] = useState('');

  const normalized = normalize(url);
  const isValid = normalized.length > 0;

  const handleAdd = () => {
    if (!isValid) return;
    onAdd({ url: normalized, label: label.trim() });
  };

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-[58] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="add-link-modal-overlay"
    >
      <div
        className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md"
        onClick={(e) => e.stopPropagation()}
        data-testid="add-link-modal"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100 dark:border-white/10">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-green-100 dark:bg-green-500/20 flex items-center justify-center shrink-0">
              <Link2 className="w-4 h-4 text-green-700 dark:text-green-400" />
            </div>
            <div className="min-w-0">
              <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Attach Link</h3>
              {songTitle && (
                <p className="text-xs text-slate-500 dark:text-slate-400 truncate max-w-[220px]">{songTitle}</p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="close-link-modal-btn"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-white/10 transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Body */}
        <div className="px-5 py-4 space-y-4">
          {/* URL input */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
              URL
            </label>
            <input
              type="url"
              placeholder="Paste a link…"
              data-testid="link-url-input"
              value={url}
              autoFocus
              onChange={(e) => setUrl(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              className="w-full h-10 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 dark:focus:ring-green-500/20 transition-all"
            />
          </div>

          {/* Label input */}
          <div>
            <label className="block text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">
              Label <span className="font-normal normal-case text-slate-400">(optional)</span>
            </label>
            <input
              type="text"
              placeholder="e.g. Live version, Verse 1 reference…"
              data-testid="link-label-input"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAdd(); } }}
              className="w-full h-10 px-3 text-sm border border-slate-200 dark:border-slate-700 rounded-xl bg-white dark:bg-slate-800 text-slate-900 dark:text-white placeholder-slate-400 outline-none focus:border-green-400 focus:ring-2 focus:ring-green-100 dark:focus:ring-green-500/20 transition-all"
            />
          </div>

          {/* Live rich preview */}
          {isValid && (
            <div data-testid="link-preview-section">
              <p className="text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-2">
                Preview
              </p>
              <LinkPreview url={normalized} label={label.trim()} />
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex gap-2.5 px-5 py-4 border-t border-slate-100 dark:border-white/10">
          <button
            onClick={onClose}
            data-testid="cancel-add-link-btn"
            className="flex-1 h-10 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-300 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleAdd}
            data-testid="confirm-add-link-btn"
            disabled={!isValid}
            className="flex-1 h-10 rounded-xl text-sm font-medium text-white bg-green-700 hover:bg-green-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            Add Link
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddLinkModal;
